import React, { useContext, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import Moment from "moment-timezone";

import { AuthContext } from "../contexts/AuthContext";
import { ScreenContext } from "../contexts/ScreenContext";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import LinearProgress from "@material-ui/core/LinearProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

const Settings = () => {
  const classes = useStyles();
  const { user, token, verify } = useContext(AuthContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

  const [isLoading, setIsLoading] = useState(false);
  
  // Account settings state
  const [email, setEmail] = useState(user.email || '');
  const [togglApi, setTogglApi] = useState(user.tkey || '');
  const [timezone, setTimezone] = useState(user.timezone || 'America/New_York');
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Get all available timezones from moment-timezone
  const timezones = Moment.tz.names();

  const handleUpdateAccountSettings = async () => {
    if (!email) {
      setSnackbarMessage("Email is required");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        API_URL + 'update-my-settings',
        {
          email: email,
          toggl_api: togglApi,
          timezone: timezone
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage("Account settings updated successfully");
      
      // Update token and user context with fresh data from backend
      if (response.data.token && response.data.user) {
        localStorage.setItem('token', response.data.token);
        await verify(response.data.token);
      }
    } catch (error) {
      console.error("Update settings error:", error);
      setSnackbarMessage(error.response?.data?.error || "Failed to update settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setSnackbarMessage("All password fields are required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setSnackbarMessage("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setSnackbarMessage("New password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        API_URL + 'update-my-settings',
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSnackbarMessage("Password changed successfully");
      
      // Update token with fresh data from backend
      if (response.data.token && response.data.user) {
        localStorage.setItem('token', response.data.token);
        await verify(response.data.token);
      }
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error("Change password error:", error);
      setSnackbarMessage(error.response?.data?.error || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h4" gutterBottom>
        My Settings
      </Typography>

      {isLoading && <LinearProgress />}

      {/* Account Information Section */}
      <Paper className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Account Information
        </Typography>
        
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            <strong>Name:</strong> {user.first_name} {user.last_name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Username:</strong> {user.username}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>User Type:</strong> {user.type}
          </Typography>
        </Box>

        <Divider style={{ margin: '16px 0' }} />

        <TextField
          id="settings-email"
          name="email"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          className={classes.formField}
          required
        />

        <TextField
          id="settings-toggl-api"
          name="togglApi"
          label="Toggl API Key"
          value={togglApi}
          onChange={(e) => setTogglApi(e.target.value)}
          fullWidth
          className={classes.formField}
          placeholder="Optional"
          helperText="Used for time tracking integration"
        />

        <Autocomplete
          id="settings-timezone"
          options={timezones}
          value={timezone}
          onChange={(event, newValue) => {
            if (newValue) {
              setTimezone(newValue);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Timezone"
              className={classes.formField}
              helperText="Used for Toggl time entry conversions"
            />
          )}
          fullWidth
        />

        <Box className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateAccountSettings}
            disabled={isLoading}
          >
            Save Account Settings
          </Button>
        </Box>
      </Paper>

      {/* Change Password Section */}
      <Paper className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Change Password
        </Typography>

        <TextField
          id="settings-current-password"
          name="currentPassword"
          label="Current Password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          fullWidth
          className={classes.formField}
          required
        />

        <TextField
          id="settings-new-password"
          name="newPassword"
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
          className={classes.formField}
          required
          helperText="Minimum 6 characters"
        />

        <TextField
          id="settings-confirm-password"
          name="confirmPassword"
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          className={classes.formField}
          required
        />

        <Box className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleChangePassword}
            disabled={isLoading}
          >
            Change Password
          </Button>
        </Box>
      </Paper>
    </div>
  );
};

export default Settings;
